// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { CustomContentProps } from 'notistack';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppIcon } from '../app-icon';

/**
 * Custom notistack content component for the "syncingFailed" variant. Use when sync
 * fails: enqueueSnackbar(message, { variant: 'syncingFailed' }).
 */
export const SyncFailedSnackbar = forwardRef<HTMLDivElement, CustomContentProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const label = t('aggregator.sync-failed-label', { ns: 'aggregator' });
    const message = props.message ?? '';

    return (
      <div
        ref={ref}
        role="alert"
        style={props.style}
        className={props.className}
        data-testid="sync-failed-snackbar"
      >
        <div className="flex min-w-0 max-w-full items-center gap-3 rounded-full bg-white py-2 pl-2 pr-4 shadow-md sm:min-w-[280px]">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-negative/10">
            <AppIcon icon="ExclamationMark" size="lg" color="danger" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wide text-negative">
              {label}
            </p>
            <p className="truncate text-base font-semibold text-secondary-dark">
              {message}
            </p>
          </div>
        </div>
      </div>
    );
  },
);

SyncFailedSnackbar.displayName = 'SyncFailedSnackbar';
